import { Spinner, SpinnerSize, TextField } from "office-ui-fabric-react";
import * as React from "react";
import Partner from "../../../classes/Partner";
import Project from "../../../classes/Project";
import { ContentType, HttpVerb, requestWithAuth } from "../../../utils/httpRequest";
import { OdooTheme } from "../../../utils/Themes";
import { _t } from "../../../utils/Translator";
import api from "../../api";
import "./SelectProjectDropdown.css";
import AppContext from "../AppContext";
import { isMockMode } from "../../../mock/mockInterceptor";

type SelectProjectProps = {
  partner: Partner;
  canCreateProject: boolean;
  onProjectClick: (project: Project, targetWindow?: Window | null) => void;
};

type SelectProjectState = {
  query: string;
  isLoading: boolean;
  projects: Project[];
};

class SelectProjectDropdown extends React.Component<SelectProjectProps, SelectProjectState> {
  constructor(props, context) {
    super(props, context);
    this.state = { query: "", isLoading: false, projects: [] };
  }

  private projectsRequest;

  private onQueryChanged = event => {
    const query = event.target.value;
    this.setState({ query: query });
    this.cancelProjectsRequest();
    if (query.length > 0) {
      this.getProjectsRequest(query);
    } else {
      this.setState({ isLoading: false, projects: [] });
    }
  };

  private cancelProjectsRequest = () => {
    if (this.projectsRequest) this.projectsRequest.cancel();
  };

  private getProjectsRequest = async (searchTerm: string) => {
    if (!searchTerm || !searchTerm.length) {
      return;
    }

    this.setState({ isLoading: true });

    this.projectsRequest = requestWithAuth(HttpVerb.PATCH, api.baseURL + api.searchProject, ContentType.Json, {
      args: [[["name", "ilike", searchTerm]]],
      fields: ["id", "name", "company_id"]
    });

    this.context.addRequestCanceller(this.projectsRequest.cancel);

    let parsed = null;
    try {
      const { responseText } = await this.projectsRequest.promise;
      if (!responseText) {
        this.setState({ projects: [], isLoading: false });
        return;
      }
      parsed = JSON.parse(responseText);
    } catch (error) {
      const err = error as Error & { status?: number };
      if (err && err.message !== "Request cancelled") {
        console.log(err);
        this.setState({ isLoading: false, projects: [] });
        this.context.showHttpErrorMessage(error);
      } else {
        this.setState({ projects: [], isLoading: false });
      }
      return;
    }

    try {
      if (parsed.success && Array.isArray(parsed.data)) {
        const projects = parsed.data.map(project_json => Project.fromJson(project_json));
        this.setState({ projects: projects, isLoading: false });
      } else {
        this.setState({ projects: [], isLoading: false });
      }
    } catch (error) {
      this.setState({ projects: [], isLoading: false });
      console.log(error);
    }
  };

  private createProject = async () => {
    // This click is the last synchronous user gesture before two async create
    // requests. Reserve the tab here so the browser does not block it later.
    const targetWindow = isMockMode() ? null : window.open("", "_blank");
    const createProjectRequest = requestWithAuth(HttpVerb.PATCH, api.baseURL + api.createProject, ContentType.Json, {
      args: [
        {
          name: this.state.query
        }
      ]
    });

    this.setState({ isLoading: true });

    let response = null;
    try {
      const { responseText } = await createProjectRequest.promise;
      response = JSON.parse(responseText);
    } catch (error) {
      if (targetWindow && !targetWindow.closed) {
        targetWindow.close();
      }
      if (error) {
        this.setState({ isLoading: false, projects: [] });
        this.context.showHttpErrorMessage(error);
        this.setState({ isLoading: false });
      }
      return;
    }

    if (!response.success) {
      if (targetWindow && !targetWindow.closed) {
        targetWindow.close();
      }
      this.setState({ isLoading: false, projects: [] });
      this.context.showHttpErrorMessage(new Error(response.error || "Failed to create project"));
      return;
    }

    const createdProject: Project = {
      id: response.id || (response.data && response.data.id),
      name: null,
      company_id: null
    };

    this.props.onProjectClick(createdProject, targetWindow);
  };

  private getProjects = () => {
    const searchedTermExists = this.state.projects.filter(p => p.name.toUpperCase() === this.state.query.toUpperCase())
      .length;

    const allowCreateNewProject = this.props.canCreateProject && !!this.state.query.length && !searchedTermExists;

    return (
      <div>
        {this.state.projects.map(project => (
          <div
            key={project.id}
            className="project-search-result-text"
            onClick={() => this.props.onProjectClick(project)}
          >
            {project.name}
          </div>
        ))}
        {allowCreateNewProject && (
          <div className="create-project-text" onClick={this.createProject}>
            {_t("Create %(name)s", { name: this.state.query })}
          </div>
        )}
        {this.state.query.length && !allowCreateNewProject && !this.state.projects.length ? (
          <div>{_t("No Project Found")}</div>
        ) : null}
        {this.state.isLoading && (
          <Spinner theme={OdooTheme} size={SpinnerSize.large} className="project-result-spinner" />
        )}
      </div>
    );
  };

  render() {
    return (
      <div className="project-result-container">
        <div>{_t("Pick a Project to create a Task")}</div>
        <div className="project-search-bar">
          <TextField
            className="input-search"
            placeholder={_t("Search Projects...")}
            onChange={this.onQueryChanged}
            value={this.state.query}
            autoComplete="off"
            onFocus={e => e.target.select()}
          />
        </div>
        {this.getProjects()}
      </div>
    );
  }
}

SelectProjectDropdown.contextType = AppContext;
export default SelectProjectDropdown;
